import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RichContent from '../common/RichContent';
import { ROUTES } from '../../constants/routes';
import { resolveLessonDisplayStatus } from '../../utils/courseMappers';
import { hasPlayableVideoTarget } from '../../utils/vdocipherMappers';

function SubscribedStatusIcon({ status }) {
  if (status === 'completed') {
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#69991D]">
        <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }

  if (status === 'in-progress') {
    return (
      <span className="relative flex h-6 w-6 items-center justify-center">
        <svg className="h-6 w-6 -rotate-90" viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="9" fill="none" stroke="#D9EEF8" strokeWidth="3" />
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="none"
            stroke="#1A87CC"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="56.5"
            strokeDashoffset="28"
          />
        </svg>
      </span>
    );
  }

  // Available (subscribed): open lock — clear shackle gap vs closed lock
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#009DA8]">
      <svg className="h-3.5 w-3.5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M18 8h-1V6A5 5 0 007.35 3.65a1 1 0 101.4 1.42A3 3 0 0115 6v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2zm-6 9a2 2 0 110-4 2 2 0 010 4z" />
      </svg>
    </span>
  );
}

function LockedStatusIcon() {
  return (
    <svg className="h-5 w-5 text-[#1A87CC]" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function LessonRow({ item, hasFullAccess, onPlayLesson }) {
  const displayStatus = resolveLessonDisplayStatus(item, hasFullAccess);
  const bgClass =
    displayStatus === 'locked'
      ? 'bg-[#FFF0E6]'
      : item.type === 'mcq'
        ? 'bg-[#FFF5F5]'
        : 'bg-[#E8F4FC]';
  const isLecture = item.type === 'lecture';
  const hoverClass = isLecture
    ? 'group transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(26,135,204,0.12)]'
    : '';

  const canPlayLecture =
    isLecture &&
    displayStatus !== 'locked' &&
    hasPlayableVideoTarget(item) &&
    (hasFullAccess || item.isFreePreview);

  const handleRowClick = () => {
    if (!canPlayLecture) return;
    onPlayLesson?.(item);
  };

  return (
    <div
      role={canPlayLecture ? 'button' : undefined}
      tabIndex={canPlayLecture ? 0 : undefined}
      onClick={handleRowClick}
      onKeyDown={(event) => {
        if (!canPlayLecture) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPlayLesson?.(item);
        }
      }}
      className={`mx-3 mb-2 flex items-start justify-between gap-3 rounded-lg px-4 py-3 ${bgClass} ${hoverClass} ${
        canPlayLecture ? 'cursor-pointer hover:brightness-[0.99]' : ''
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {isLecture && (
          <svg
            className="mt-0.5 h-4 w-4 shrink-0 text-[#1A87CC] transition-transform duration-300 ease-out group-hover:translate-x-0.5"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
        {item.type === 'notes' && (
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#E85D5D]" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        )}
        {item.type === 'mcq' && (
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-[#E85D5D]" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[#1B1B1B] transition-colors duration-300 group-hover:text-[#146599]">
            {item.title}
          </p>
          {item.subtitle ? (
            <RichContent
              plain
              content={item.subtitle}
              className="mt-1 line-clamp-2 text-sm leading-snug text-[#444444]"
            />
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 self-center">
        {item.duration && (
          <span className="flex items-center gap-1 text-xs font-medium text-[#666666] transition-colors duration-300 group-hover:text-[#146599]">
            <svg className="h-3.5 w-3.5 text-[#1A87CC]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {item.duration}
          </span>
        )}

        {displayStatus === 'locked' && (
          <Link
            to={ROUTES.PRICING}
            onClick={(event) => event.stopPropagation()}
            className="flex items-center gap-1 rounded-lg bg-[#E85D5D] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#d44f4f]"
          >
            Unlock Now
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </Link>
        )}

        {item.type === 'mcq' && item.status === 'resource' && (
          hasFullAccess ? (
            <>
              <a
                href={item.fileUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded-lg bg-[#9369CC] px-3 py-1.5 text-xs font-semibold text-white"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View PDF
              </a>
              <a
                href={item.fileUrl || '#'}
                download
                className="flex items-center gap-1 rounded-lg border border-[#9369CC] bg-[#F3F0FF] px-3 py-1.5 text-xs font-semibold text-[#9369CC]"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                </svg>
                Download
              </a>
            </>
          ) : (
            <>
              <button type="button" className="flex items-center gap-1 rounded-lg bg-[#9369CC] px-3 py-1.5 text-xs font-semibold text-white">
                View PDF
              </button>
              <button type="button" className="flex items-center gap-1 rounded-lg bg-[#9369CC] px-3 py-1.5 text-xs font-semibold text-white">
                Download
              </button>
            </>
          )
        )}

        {hasFullAccess && item.type === 'lecture' && displayStatus !== 'locked' && (
          <SubscribedStatusIcon status={displayStatus} />
        )}

        {!hasFullAccess && item.isFreePreview && item.type === 'lecture' && displayStatus !== 'locked' && (
          <span className="rounded-full bg-[#1A87CC]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1A87CC]">
            Preview
          </span>
        )}

        {!hasFullAccess && displayStatus !== 'locked' && item.type === 'lecture' && !item.isFreePreview && (
          <LockedStatusIcon />
        )}
      </div>
    </div>
  );
}

function ChapterAccordion({ chapter, expanded, onToggle, hasFullAccess, onPlayLesson }) {
  const progressLabel = chapter.progress
    ? `${chapter.progress.completed}/${chapter.progress.total}`
    : `${chapter.progressPercent}% Completed`;

  return (
    <div className="overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-col gap-3 px-5 py-4 text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-bold text-[#146599]">{chapter.title}</h3>
          <svg
            className={`h-5 w-5 shrink-0 text-[#666666] transition ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-[3px] flex-1 overflow-hidden rounded-full bg-[#E9E9E9]">
            <div
              className="h-full rounded-full bg-[#1A87CC]"
              style={{ width: `${chapter.progressPercent || 0}%` }}
            />
          </div>
          <span className="shrink-0 text-xs font-semibold text-[#1A87CC]">{progressLabel}</span>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-[#666666]">
          <span className="flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1A87CC]">
              <svg className="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            {chapter.lessonsCount} Lessons
          </span>
          <span className="flex items-center gap-1.5">
            <svg className="h-4 w-4 text-[#E85D5D]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
            </svg>
            {chapter.pdfCount} PDF
          </span>
          <span className="flex items-center gap-1.5">
            <svg className="h-4 w-4 text-[#69991D]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {chapter.duration}
          </span>
        </div>
      </button>

      {expanded && chapter.items.length > 0 && (
        <div className="border-t border-gray-100 pb-2 pt-1">
          {chapter.items.map((item) => (
            <LessonRow
              key={item.id}
              item={item}
              hasFullAccess={hasFullAccess}
              onPlayLesson={onPlayLesson}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function getChapterKey(chapter, index) {
  return `ch-${index}-${chapter?.id ?? 'x'}`;
}

function CourseContentSection({ chapters = [], hasFullAccess = false, course, courseId }) {
  const navigate = useNavigate();
  const [openKeys, setOpenKeys] = useState(() => {
    if (!chapters.length) return [];
    const index = chapters.findIndex((ch) => ch.expanded);
    const openIndex = index >= 0 ? index : 0;
    return [getChapterKey(chapters[openIndex], openIndex)];
  });

  const allKeys = chapters.map((chapter, index) => getChapterKey(chapter, index));
  const allExpanded =
    allKeys.length > 0 && allKeys.every((key) => openKeys.includes(key));

  const handleToggle = (chapter, index) => {
    const key = getChapterKey(chapter, index);
    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((itemKey) => itemKey !== key) : [...prev, key]
    );
  };

  const handleExpandAll = () => {
    setOpenKeys(allExpanded ? [] : allKeys);
  };

  const handlePlayLesson = (lesson) => {
    if ((!course && !courseId) || !lesson?.id) return;
    navigate(`${ROUTES.COURSE_PLAYLIST(course || courseId)}?lesson=${lesson.id}`);
  };

  return (
    <section id="course-content">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-[#146599]">Course Content</h2>
        <button
          type="button"
          onClick={handleExpandAll}
          className="flex items-center gap-1 text-sm font-semibold text-[#1A87CC] hover:underline"
        >
          {allExpanded ? 'Collapse All' : 'Expand All'}
          <svg
            className={`h-4 w-4 transition ${allExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="mt-5 space-y-4">
        {chapters.map((chapter, index) => {
          const key = getChapterKey(chapter, index);
          return (
            <ChapterAccordion
              key={key}
              chapter={chapter}
              expanded={openKeys.includes(key)}
              onToggle={() => handleToggle(chapter, index)}
              hasFullAccess={hasFullAccess}
              onPlayLesson={handlePlayLesson}
            />
          );
        })}
      </div>
    </section>
  );
}

export default CourseContentSection;
